// apps/web/src/components/onboarding/RulerPicker.tsx
// A horizontal ruler dial you drag left/right to pick a value — v2.
//
// v1 was a native overflow-x scroller. On device that could never feel right:
// WKWebView delivers scroll events asynchronously while the compositor moves
// the strip, so the value lagged the finger, and long tick strips rasterize
// in tiles — labels visibly popped in and out during flings (owner: "numbers
// hide and appear from the screen"). This version owns the whole gesture:
// pointer events move an offset in the SAME frame, release hands off to a
// small inertia loop with rubber-band ends and a snap, and every visible tick
// is redrawn onto a canvas each frame so nothing can ever pop. The edges fade
// under a mask instead of hard-clipping at the screen edge.
import { useEffect, useRef } from "react";
import { hapticTick } from "../../lib/haptics";

interface Props {
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
  /** Draw a taller, labelled tick every N steps. */
  majorEvery?: number;
}

const PX = 12;            // px per step
const H = 64;             // canvas CSS height
const FRICTION = 0.94;    // inertia decay per 60fps frame
const STOP_V = 0.02;      // px/ms — below this the fling is over
const OOB_DRAG = 0.35;    // finger resistance past the ends
const SNAP_EASE = 0.22;   // settle: fraction of remaining distance per frame

export function RulerPicker({ min, max, step = 1, value, onChange, majorEvery = 10 }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cbRef = useRef(onChange);
  cbRef.current = onChange;

  const count = Math.max(1, Math.round((max - min) / step));

  // All motion state lives in refs: the canvas is drawn imperatively, and a
  // React re-render (each live onChange) must never reset an in-flight fling.
  const s = useRef({
    offset: 0,            // px from min to the centered position (unclamped)
    vel: 0,               // px/ms, + = value increasing
    dragging: false,
    settling: false,
    raf: 0,
    lastX: 0,
    lastT: 0,
    lastIdx: 0,
    w: 0,
    colors: { minor: "#8884", major: "#888", label: "#888" },
    reduced: false,
  });

  // ── drawing ───────────────────────────────────────────────────────────────
  const draw = () => {
    const cv = canvasRef.current;
    const st = s.current;
    if (!cv || !st.w) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const maxOff = count * PX;
    // rubber-band: the DISPLAYED offset resists past the ends
    const off = st.offset < 0
      ? st.offset * OOB_DRAG
      : st.offset > maxOff ? maxOff + (st.offset - maxOff) * OOB_DRAG : st.offset;
    const cx = st.w / 2;
    ctx.clearRect(0, 0, st.w, H);
    ctx.textAlign = "center";
    ctx.font = "500 11px -apple-system, system-ui, sans-serif";
    const first = Math.max(0, Math.floor((off - cx) / PX));
    const last = Math.min(count, Math.ceil((off + cx) / PX));
    for (let i = first; i <= last; i++) {
      const x = Math.round(cx + i * PX - off);
      if (i % majorEvery === 0) {
        ctx.fillStyle = st.colors.major;
        ctx.fillRect(x - 1, 6, 2, 28);
        ctx.fillStyle = st.colors.label;
        ctx.fillText(String(min + i * step), x, 50);
      } else {
        ctx.fillStyle = st.colors.minor;
        ctx.fillRect(x, 14, 1, 14);
      }
    }
  };

  // Live value emission + tick haptic whenever a new step crosses the pointer.
  const emit = () => {
    const st = s.current;
    const idx = Math.min(Math.max(0, Math.round(st.offset / PX)), count);
    if (idx !== st.lastIdx) {
      st.lastIdx = idx;
      hapticTick();
      cbRef.current(min + idx * step);
    }
  };

  // ── inertia + snap loop (runs only while moving) ─────────────────────────
  const loop = (now: number) => {
    const st = s.current;
    const dt = Math.min(50, now - st.lastT || 16.7);
    st.lastT = now;
    const maxOff = count * PX;

    if (!st.settling) {
      st.offset += st.vel * dt;
      // decay; extra drag while out of bounds so the band pulls short
      const out = st.offset < 0 || st.offset > maxOff;
      st.vel *= Math.pow(out ? FRICTION * 0.82 : FRICTION, dt / 16.7);
      emit();
      if (Math.abs(st.vel) < STOP_V) st.settling = true;
    }
    if (st.settling) {
      const target = Math.min(Math.max(0, Math.round(st.offset / PX)), count) * PX;
      st.offset += (target - st.offset) * SNAP_EASE;
      if (Math.abs(target - st.offset) < 0.4) {
        st.offset = target;
        st.settling = false;
        emit();
        draw();
        st.raf = 0;
        return; // motion finished
      }
    }
    draw();
    st.raf = requestAnimationFrame(loop);
  };
  const startLoop = () => {
    const st = s.current;
    if (!st.raf) { st.lastT = performance.now(); st.raf = requestAnimationFrame(loop); }
  };

  // ── gestures ─────────────────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    const st = s.current;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    if (st.raf) { cancelAnimationFrame(st.raf); st.raf = 0; }
    st.dragging = true;
    st.settling = false;
    st.vel = 0;
    st.lastX = e.clientX;
    st.lastT = performance.now();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const st = s.current;
    if (!st.dragging) return;
    const now = performance.now();
    const dx = e.clientX - st.lastX;
    const dt = Math.max(1, now - st.lastT);
    st.lastX = e.clientX;
    st.lastT = now;
    const maxOff = count * PX;
    const out = st.offset < 0 || st.offset > maxOff;
    st.offset -= dx * (out ? OOB_DRAG : 1); // drag left = higher value
    // smoothed release velocity
    st.vel = 0.75 * (-dx / dt) + 0.25 * st.vel;
    emit();
    draw();
  };
  const onPointerUp = () => {
    const st = s.current;
    if (!st.dragging) return;
    st.dragging = false;
    if (st.reduced) {
      // reduced motion: no fling, no spring — land on the nearest tick now
      st.vel = 0;
      st.offset = Math.min(Math.max(0, Math.round(st.offset / PX)), count) * PX;
      emit();
      draw();
      return;
    }
    st.settling = Math.abs(st.vel) < STOP_V;
    startLoop();
  };

  // ── mount / props sync ───────────────────────────────────────────────────
  useEffect(() => {
    const st = s.current;
    st.reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const wrap = wrapRef.current;
    const cv = canvasRef.current;
    if (!wrap || !cv) return;
    // theme tokens resolved once — the canvas can't use var()
    const cs = getComputedStyle(wrap);
    st.colors = {
      minor: cs.getPropertyValue("--line").trim() || "#8884",
      major: cs.getPropertyValue("--fg-muted").trim() || "#888",
      label: cs.getPropertyValue("--fg-muted").trim() || "#888",
    };
    const ro = new ResizeObserver(() => {
      const w = wrap.clientWidth;
      const dpr = window.devicePixelRatio || 1;
      st.w = w;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(H * dpr);
      cv.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw();
    });
    ro.observe(wrap);
    return () => {
      ro.disconnect();
      if (st.raf) cancelAnimationFrame(st.raf);
      st.raf = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // External value / range change (unit toggle, mount): jump the dial there —
  // no animation, no haptic, and never while the finger owns it.
  useEffect(() => {
    const st = s.current;
    if (st.dragging || st.raf) return;
    const idx = Math.min(Math.max(0, Math.round((value - min) / step)), count);
    st.offset = idx * PX;
    st.lastIdx = idx;
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, min, max, step, count]);

  return (
    <div className="relative select-none">
      {/* center pointer */}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 z-10 h-9 w-[3px] -translate-x-1/2 rounded-full bg-brand" />
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-9 z-10 -translate-x-1/2 border-x-[5px] border-t-[6px] border-x-transparent border-t-brand" />
      <div
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          touchAction: "none",
          cursor: "grab",
          WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 14%, #000 86%, transparent 100%)",
          maskImage: "linear-gradient(90deg, transparent 0%, #000 14%, #000 86%, transparent 100%)",
        }}
      >
        <canvas ref={canvasRef} style={{ width: "100%", height: H, display: "block" }} aria-hidden="true" />
      </div>
    </div>
  );
}
